import type { ToolDef } from "@fabric-harness/sdk";
import {
  assertExactResourceAllowed,
  pinnedStringResourceSchema,
  resolveExactResourcePolicy,
} from "./resource-policy.js";
import { type DatabricksStatementClient, runStatement } from "./sql-exec.js";

export interface DatabricksAiQueryToolOptions {
  warehouseId: string;
  name?: string;
  description?: string;
  pollIntervalMs?: number;
  timeoutMs?: number;
}
export type DatabricksAiQueryPolicy =
  | { allowedEndpoints: readonly string[]; allowAnyEndpoint?: never }
  | { allowedEndpoints?: never; allowAnyEndpoint: true };

/**
 * `ai_query(endpoint, request)` — run model inference inside the SQL warehouse against a serving
 * endpoint. Drives both serving and SQL-warehouse consumption. Uses named SQL parameters, so the
 * endpoint and request are never string-interpolated into SQL.
 */
export function databricksAiQueryTool(
  client: DatabricksStatementClient,
  endpointPolicy: DatabricksAiQueryPolicy,
  options: DatabricksAiQueryToolOptions,
): ToolDef<{ endpoint: string; request: string }, unknown> {
  const poll = {
    intervalMs: options.pollIntervalMs ?? 2000,
    timeoutMs: options.timeoutMs ?? 120_000,
  };
  const allowedEndpoints = resolveExactResourcePolicy(
    endpointPolicy,
    "allowedEndpoints",
    "allowAnyEndpoint",
    "AI Functions endpoint",
  );
  return {
    name: options.name ?? "databricks_ai_query",
    description:
      options.description ??
      "Run model inference inside the SQL warehouse via ai_query() against a Databricks serving endpoint.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        endpoint: pinnedStringResourceSchema(allowedEndpoints),
        request: { type: "string" },
      },
      required: ["endpoint", "request"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "ai-functions",
      governance: {
        resources: [
          { kind: "serving-endpoint", path: "/endpoint" },
          { kind: "sql-warehouse", value: options.warehouseId },
        ],
      },
    },
    async execute(input) {
      assertExactResourceAllowed("AI Functions endpoint", input.endpoint, allowedEndpoints);
      return await runStatement(
        client,
        options.warehouseId,
        "SELECT ai_query(:endpoint, :request) AS result",
        [
          { name: "endpoint", value: input.endpoint },
          { name: "request", value: input.request },
        ],
        poll,
      );
    },
  };
}
